/**
 * Why a party suits somebody.
 *
 * A compatibility score out of a hundred is a number nobody can argue with
 * and nobody believes. These are the actual reasons, in the app's own words -
 * same language, plays what you play, welcomes newcomers - so a person can
 * see the reasoning and disagree with it.
 *
 * Only ever positive: this explains why something is being suggested, it is
 * not a list of what is wrong with a party.
 */

import type { Party, PublicUser } from './types';

export type Reason = { id: string; label: string; icon: string };

export function reasonsFor(party: Party, me: PublicUser | null | undefined, gameName?: string): Reason[] {
  if (!me) return [];
  const out: Reason[] = [];

  if ((me.games ?? []).some((g) => g.id === party.gameId)) {
    out.push({ id: 'game', label: gameName ? 'You play ' + gameName : 'A game you play', icon: 'dice' });
  }
  if (party.region && me.region && party.region === me.region) {
    out.push({ id: 'region', label: 'Your region', icon: 'globe' });
  }
  if (party.language && (me.languages ?? []).includes(party.language)) {
    out.push({ id: 'language', label: 'Speaks ' + party.language, icon: 'translate' });
  }
  if (party.newcomers) {
    out.push({ id: 'new', label: 'New players welcome', icon: 'heart' });
  }
  /* Playstyle lives on the party as a tag and on the person as one word. */
  if (me.playstyle && (party.tags ?? []).some((t) => t.toLowerCase().includes(me.playstyle!.toLowerCase().split(' ')[0] ?? ''))) {
    out.push({ id: 'style', label: 'Same playstyle', icon: 'target' });
  }
  const mine = me.platforms ?? [];
  const theirs = party.platforms ?? [];
  if (mine.length && theirs.length && theirs.some((p) => mine.includes(p))) {
    out.push({ id: 'platform', label: 'Same platform', icon: 'monitor' });
  }
  if (!party.micRequired && me.mic === false) {
    out.push({ id: 'text', label: 'Microphone optional', icon: 'chat' });
  }

  return out.slice(0, 3);
}

/**
 * One filter to loosen, when nothing matched.
 *
 * Named one at a time rather than as "clear everything": the point of a
 * suggestion is that you keep the part of the search you care about.
 */
export type Loosen = { id: string; label: string; apply: () => void };

export function loosenSuggestions(opts: {
  game: string;
  region: string;
  search: string;
  micOnly: boolean;
  length: string;
  platform: string;
  newcomers: boolean;
  gameName?: string;
  regionName?: string;
  clear: (what: 'game' | 'region' | 'search' | 'mic' | 'length' | 'platform' | 'newcomers') => void;
}): Loosen[] {
  const out: Loosen[] = [];
  if (opts.region) out.push({ id: 'region', label: 'Anywhere, not just ' + (opts.regionName || opts.region), apply: () => opts.clear('region') });
  if (opts.length) out.push({ id: 'length', label: 'Any length of session', apply: () => opts.clear('length') });
  if (opts.platform) out.push({ id: 'platform', label: 'Any platform', apply: () => opts.clear('platform') });
  if (opts.micOnly) out.push({ id: 'mic', label: 'Microphone not required', apply: () => opts.clear('mic') });
  if (opts.newcomers) out.push({ id: 'newcomers', label: 'Any party, not only beginner friendly', apply: () => opts.clear('newcomers') });
  if (opts.search) out.push({ id: 'search', label: 'Drop “' + opts.search + '”', apply: () => opts.clear('search') });
  /* The game goes last: it is almost always the part somebody means to keep. */
  if (opts.game) out.push({ id: 'game', label: 'Any game, not just ' + (opts.gameName || opts.game), apply: () => opts.clear('game') });
  return out.slice(0, 3);
}
